/**
 * `ReanchorClient` over HTTP to the core's `/api/v1/reanchor-resolve`. It holds
 * no state: every call sends the live document and the anchor the memory
 * describes, so nothing the server answers depends on an earlier call.
 *
 * Every way the call can fail answers null rather than throwing, because this
 * is the last and most expensive recovery tier: `ServerStep` runs only after
 * the three cheaper tiers came up empty, and an exception escaping here would
 * unwind `RecoveryChain` and `AnchorTracker` — neither of which catches — and
 * throw their work away along with the anchor-log record of the attempt.
 */
import { getApiKey } from "../../addin-auth";
import { getEnv } from "../../envManager";
import { safeFetch } from "../../safe-fetch";
import { type ReanchorClient, type ReanchorRequest, type ReanchorResponse, reanchorResponseSchema } from "../reanchor-client";

export class HttpReanchorClient implements ReanchorClient {
    async resolve(request: ReanchorRequest): Promise<ReanchorResponse | null> {
        const apiKey = getApiKey();
        /**
         * WHEN no API key is available for this session
         *   CONSEQUENCE
         *     Return null: this resolve attempt cannot be sent to the server yet.
         *
         *   NULL
         *     No server recovery result. The caller should treat this attempt as unresolved.
         */
        if (!apiKey) return null;
        try {
            const response = await safeFetch(`${getEnv("API_BASE_URL")}/reanchor-resolve`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
                body: JSON.stringify(request),
            });
            return reanchorResponseSchema.parse(await response.json());
        } catch {
            /**
             * WHEN the request does not come back as a result this client can answer with
             *   CONSEQUENCE
             *     Return null rather than throwing, which is what the declared
             *     `Promise<ReanchorResponse | null>` promises and what `ServerStep`
             *     is written against.
             *
             * WHY a catch rather than a status test
             *   `safeFetch` never hands back a response that is not OK: it throws
             *   `makeHttpError` for every non-2xx and returns only OK responses. So a
             *   `!response.ok` test here can never be true, and every real failure —
             *   a 500, a 401, an unreachable endpoint, a body that is not a
             *   `ReanchorResponse` — arrives as a throw instead.
             *
             * CASES
             *   - the server answered an error status  -> null
             *   - the endpoint was unreachable         -> null
             *   - the answer did not fit the schema    -> null
             *
             * NULL
             *   No server recovery result from this attempt. `ServerStep` records the
             *   attempt in the anchor log and reports it as not found, leaving the three
             *   cheaper tiers' work intact instead of unwinding the whole recovery.
             */
            return null;
        }
    }
}
